/**
 * variantCardLabel -- single-source labeling for a decoration "color card"
 * (a Variant row).
 *
 * A color card's user-facing name follows this priority, IDENTICAL to the
 * product-editor VariantCard.vue header (which imports from this module so
 * the two surfaces can never drift):
 *
 *   1. combined-shade pair -> the fixed "All colors (one artwork)" label.
 *      When a placement's catch_all_light and catch_all_dark variants point
 *      at the SAME artwork, the product editor collapses them into ONE card
 *      with this fixed label. See ``collapseCombinedShade`` below.
 *   2. a custom (distributor-authored) ``name`` -- trimmed, non-empty --
 *      wins over the kind-derived label.
 *   3. otherwise the kind-derived label from ``KIND_HEADER_LABELS``, with a
 *      kind-agnostic "Artwork" fallback for an unknown/absent kind.
 *
 * These are the SAME strings the product editor shows. Do NOT fork this
 * scheme -- import from here.
 */

// PR-N4 kind-aware header labels. Mirrors the DB ``variants.kind`` enum
// (api/src/models/variant.py VARIANT_KINDS). Kept frozen so callers can't
// mutate the shared table.
export const KIND_HEADER_LABELS = Object.freeze({
    catch_all_light: "Light-garment artwork",
    catch_all_dark: "Dark-garment artwork",
    single_catch_all: "Single artwork (all colors)",
    specific_colors: "Specific colors",
});

// Fixed label for a collapsed light+dark identical pair. The combined card
// speaks for all colors, so it keeps this label even if the underlying
// (light) row carries a custom name.
export const COMBINED_SHADE_LABEL = "All colors (one artwork)";

// Label for a LONE catch_all_light -- a row with no dark sibling, which per
// the two-kind model IS "all colors" (Jake, 2026-08-29). Distinct from
// COMBINED_SHADE_LABEL: that one collapses TWO rows into one card and says
// so ("one artwork"); this is a single row that genuinely routes everything.
export const ALL_COLORS_LABEL = "All colors";

// Kind-agnostic fallback (matches VariantCard's PR #1915 P2-1 wording:
// the user-facing fallback is "Artwork", never the machinery word
// "Variant").
export const VARIANT_LABEL_FALLBACK = "Artwork";

/**
 * The header/label for a single variant (color card).
 *
 * @param {{kind?: string, name?: string}|null} variant the variant row
 * @param {{combinedShade?: boolean}} [opts] combinedShade forces the
 *   fixed all-colors label (used for the collapsed light+dark card).
 * @returns {string} the display label
 */
export function variantCardLabel(variant, opts = {}) {
    if (opts.combinedShade) return COMBINED_SHADE_LABEL;
    // A lone catch_all_light routes EVERY colour, so calling it
    // "Light-garment artwork" would be a false claim about what it sews.
    // The caller supplies the fact because it lives in the sibling set (see
    // standsForAllColors below), not in the row.
    if (opts.standsForAllColors && !opts.combinedShade) {
        const lone = variant?.name;
        if (typeof lone === "string" && lone.trim().length > 0) {
            return lone.trim();
        }
        return ALL_COLORS_LABEL;
    }
    const rawName = variant?.name;
    if (typeof rawName === "string") {
        const trimmed = rawName.trim();
        if (trimmed.length > 0) return trimmed;
    }
    const kind = variant?.kind;
    return KIND_HEADER_LABELS[kind] ?? VARIANT_LABEL_FALLBACK;
}

/**
 * Collapse a placement's variant rows into display "cards", applying the
 * SAME combined-shade dedup the product editor's DecorationInspector uses:
 * a lone catch_all_light + lone catch_all_dark whose ``artwork_file_id``
 * matches (including both null) become ONE card labeled
 * "All colors (one artwork)". Every other variant renders as its own card.
 *
 * The dedup rule mirrors DecorationInspector.vue ``identicalShadePair``
 * (frontend/src/pages/distributor/store-editor/StoreProducts/Editor/
 * DecorationInspector.vue): exactly one light + one dark, same artwork_file_id.
 *
 * @param {Array<{id?: number, kind?: string, name?: string,
 *   artwork_file_id?: (string|number|null), display_order?: number}>} variants
 * @returns {Array<{key: string, label: string, combinedShade: boolean}>}
 *   one entry per displayed card, ordered by display_order then id.
 */
export function collapseCombinedShade(variants) {
    const list = Array.isArray(variants) ? variants.slice() : [];
    // Stable order: display_order asc, then id asc, matching the product
    // editor's variantIds ordering.
    list.sort((a, b) => {
        const oa = a?.display_order ?? 0;
        const ob = b?.display_order ?? 0;
        if (oa !== ob) return oa - ob;
        return (a?.id ?? 0) - (b?.id ?? 0);
    });

    const lights = list.filter((v) => v?.kind === "catch_all_light");
    const darks = list.filter((v) => v?.kind === "catch_all_dark");

    let combinedPair = null;
    if (lights.length === 1 && darks.length === 1) {
        const lightArt = lights[0]?.artwork_file_id ?? null;
        const darkArt = darks[0]?.artwork_file_id ?? null;
        const sameArtwork
            = (lightArt == null && darkArt == null)
            || (lightArt != null
                && darkArt != null
                && String(lightArt) === String(darkArt));
        if (sameArtwork) combinedPair = { light: lights[0], dark: darks[0] };
    }

    const cards = [];
    for (const v of list) {
        if (
            combinedPair
            && (v === combinedPair.dark)
        ) {
            // The dark row is folded into the combined card emitted for
            // its light partner; skip it here.
            continue;
        }
        if (combinedPair && v === combinedPair.light) {
            cards.push({
                key: `combined-${v?.id ?? "x"}`,
                label: COMBINED_SHADE_LABEL,
                combinedShade: true,
            });
            continue;
        }
        cards.push({
            key: `variant-${v?.id ?? "x"}`,
            label: variantCardLabel(v),
            combinedShade: false,
        });
    }
    return cards;
}

/**
 * Does this variant stand for ALL of the placement's colours?
 *
 * THE TWO-KIND MODEL (Jake, 2026-08-29): a ``catch_all_light`` with no dark
 * sibling IS "all colors". There is no third kind for it -- ``set_kind``
 * refuses ``single_catch_all`` as a target
 * (api/src/endpoints/decoration_variants.py:764) and the DELETE path demotes
 * a lone ``single_catch_all`` survivor back to ``catch_all_light`` (the FREE
 * THE COMPLEMENT block). The resolver falls through to the light tier for
 * every colour when nothing else claims one, so a lone light row really does
 * route everything.
 *
 * The qualifier: a ``specific_colors`` sibling CLAIMS colours away from the
 * catch-all tier, so with one present the light row no longer routes
 * everything and must not say it does. A legacy ``single_catch_all`` sibling
 * likewise means the light row is not the only catch-all.
 *
 * This is a property of the SIBLING SET, which is why it cannot be a
 * computed on the card: the card sees one row.
 *
 * @param {{kind?: string}|null} variant the row in question
 * @param {Array<{kind?: string}>} siblings ALL variants on the placement,
 *   including ``variant`` itself (it is filtered out by identity)
 * @returns {boolean}
 */
export function standsForAllColors(variant, siblings) {
    if (variant?.kind !== "catch_all_light") return false;
    const list = Array.isArray(siblings) ? siblings : [];
    for (const other of list) {
        if (other === variant) continue;
        const kind = other?.kind;
        if (
            kind === "catch_all_dark"
            || kind === "specific_colors"
            || kind === "single_catch_all"
        ) {
            return false;
        }
    }
    return true;
}

export default variantCardLabel;
